import { createContext, useContext, useState, type ReactNode } from "react";

type Language = "en" | "es";

type Translations = {
  [key: string]: {
    en: string;
    es: string;
  };
};

export const translations: Translations = {
  // Header
  "nav.services": { en: "Services", es: "Servicios" },
  "nav.about": { en: "About Us", es: "Nosotros" },
  "nav.drivers": { en: "Drive With Us", es: "Trabaja Con Nosotros" },
  "nav.apply": { en: "Apply Now", es: "Aplica Ahora" },
  "nav.contact": { en: "Contact", es: "Contacto" },
  "nav.quote": { en: "Get a Quote", es: "Cotización" },
  "nav.join": { en: "Join Our Team", es: "Únete al Equipo" },
  "header.support": { en: "24/7 Support", es: "Soporte 24/7" },
  "header.hours": { en: "Mon - Sun: 24 Hours", es: "Lun - Dom: 24 Horas" },

  // Hero
  "hero.tagline": { en: "Trusted Logistics Partner", es: "Socio Logístico de Confianza" },
  "hero.title1": { en: "Delivering", es: "Entregando" },
  "hero.title2": { en: "Excellence", es: "Excelencia" },
  "hero.title3": { en: "Across Every Mile", es: "En Cada Milla" },
  "hero.description": {
    en: "Based in Deptford, NJ, Reyes Trux Corp provides reliable freight solutions and exceptional career opportunities for professional truck drivers across the United States.",
    es: "Con sede en Deptford, NJ, Reyes Trux Corp ofrece soluciones de carga confiables y oportunidades de carrera excepcionales para conductores profesionales en todo Estados Unidos.",
  },
  "hero.services": { en: "Our Services", es: "Nuestros Servicios" },
  "hero.driveWithUs": { en: "Drive With Us", es: "Trabaja Con Nosotros" },
  "hero.yearsExp": { en: "Years Experience", es: "Años de Experiencia" },
  "hero.activeDrivers": { en: "Active Drivers", es: "Conductores Activos" },
  "hero.onTime": { en: "On-Time Delivery", es: "Entregas a Tiempo" },

  // Services
  "services.tagline": { en: "What We Do", es: "Lo Que Hacemos" },
  "services.title": {
    en: "Comprehensive Trucking & Logistics Services",
    es: "Servicios Completos de Transporte y Logística",
  },
  "services.description": {
    en: "From full truckloads to specialized freight, we deliver solutions tailored to your unique shipping needs across the United States.",
    es: "Desde cargas completas hasta fletes especializados, ofrecemos soluciones adaptadas a sus necesidades de envío en todo Estados Unidos.",
  },
  "services.ftl.title": { en: "Full Truckload (FTL)", es: "Carga Completa (FTL)" },
  "services.ftl.desc": {
    en: "Dedicated trucks for large shipments. Direct delivery with no stops, ensuring your freight arrives quickly and securely.",
    es: "Camiones dedicados para envíos grandes. Entrega directa sin paradas, asegurando que su carga llegue rápida y segura.",
  },
  "services.ltl.title": { en: "Less Than Truckload (LTL)", es: "Carga Parcial (LTL)" },
  "services.ltl.desc": {
    en: "Cost-effective solutions for smaller shipments. Share truck space without compromising on delivery quality.",
    es: "Soluciones económicas para envíos más pequeños. Comparta espacio sin comprometer la calidad de entrega.",
  },
  "services.expedited.title": { en: "Expedited Freight", es: "Carga Expedita" },
  "services.expedited.desc": {
    en: "Time-critical deliveries when every minute counts. 24/7 service with dedicated drivers for urgent shipments.",
    es: "Entregas críticas cuando cada minuto cuenta. Servicio 24/7 con conductores dedicados para envíos urgentes.",
  },
  "services.regional.title": { en: "Regional Routes", es: "Rutas Regionales" },
  "services.regional.desc": {
    en: "Efficient regional transportation throughout the East Coast and beyond. Flexible scheduling and consistent service.",
    es: "Transporte regional eficiente en toda la Costa Este y más allá. Programación flexible y servicio consistente.",
  },
  "services.specialized.title": { en: "Specialized Hauling", es: "Transporte Especializado" },
  "services.specialized.desc": {
    en: "Transport of oversized and specialized cargo with specialized equipment and trained drivers.",
    es: "Transporte de carga sobredimensionada y especializada con equipo especializado y conductores capacitados.",
  },
  "services.dedicated.title": { en: "Dedicated Services", es: "Servicios Dedicados" },
  "services.dedicated.desc": {
    en: "Custom transportation solutions with dedicated trucks and drivers for your specific business needs.",
    es: "Soluciones de transporte personalizadas con camiones y conductores dedicados para sus necesidades específicas.",
  },
  "services.cta": { en: "Request a Quote", es: "Solicitar Cotización" },
  "services.dedicated": { en: "Dedicated Equipment", es: "Equipo Dedicado" },
  "services.direct": { en: "Direct Routes", es: "Rutas Directas" },
  "services.tracking": { en: "Real-time Tracking", es: "Rastreo en Tiempo Real" },
  "services.efficient": { en: "Cost Efficient", es: "Costo Eficiente" },
  "services.flexible": { en: "Flexible Scheduling", es: "Programación Flexible" },
  "services.consolidated": { en: "Consolidated Shipping", es: "Envío Consolidado" },
  "services.247": { en: "24/7 Availability", es: "Disponibilidad 24/7" },
  "services.priority": { en: "Priority Handling", es: "Manejo Prioritario" },
  "services.guaranteed": { en: "Guaranteed Delivery", es: "Entrega Garantizada" },
  "services.eastCoast": { en: "East Coast Coverage", es: "Cobertura Costa Este" },
  "services.onTime": { en: "On-Time Performance", es: "Rendimiento Puntual" },
  "services.reliable": { en: "Reliable Service", es: "Servicio Confiable" },
  "services.trained": { en: "Trained Operators", es: "Operadores Capacitados" },
  "services.specialized.equipment": { en: "Specialized Equipment", es: "Equipo Especializado" },
  "services.safety": { en: "Safety First", es: "Seguridad Primero" },
  "services.custom": { en: "Custom Solutions", es: "Soluciones Personalizadas" },
  "services.assigned": { en: "Assigned Drivers", es: "Conductores Asignados" },
  "services.consistent": { en: "Consistent Service", es: "Servicio Consistente" },

  // Experience
  "experience.tagline": { en: "About Reyes Trux Corp", es: "Acerca de Reyes Trux Corp" },
  "experience.title": { en: "Trusted Transportation Excellence", es: "Excelencia en Transporte de Confianza" },
  "experience.desc1": {
    en: "Reyes Trux Corp, headquartered in Deptford, New Jersey, has been the backbone of reliable freight transportation across the United States. Our company is built on a foundation of trust, reliability, and commitment to excellence.",
    es: "Reyes Trux Corp, con sede en Deptford, Nueva Jersey, ha sido la columna vertebral del transporte de carga confiable en todo Estados Unidos. Nuestra empresa está construida sobre una base de confianza, fiabilidad y compromiso con la excelencia.",
  },
  "experience.desc2": {
    en: "Our success is built on treating our drivers like family, delivering on our promises, and never compromising on safety. This philosophy has earned us an excellent reputation that our customers trust.",
    es: "Nuestro éxito se basa en tratar a nuestros conductores como familia, cumplir nuestras promesas y nunca comprometer la seguridad. Esta filosofía nos ha ganado una excelente reputación en la que nuestros clientes confían.",
  },
  "experience.journey": { en: "OUR JOURNEY", es: "NUESTRA HISTORIA" },
  "experience.learnMore": { en: "Learn More", es: "Más Información" },
  "experience.joinTeam": { en: "Join Our Team", es: "Únete al Equipo" },
  "experience.value1": { en: "Family-owned and operated company", es: "Empresa familiar" },
  "experience.value2": { en: "Industry-leading driver retention rate", es: "Tasa líder de retención de conductores" },
  "experience.value3": {
    en: "Modern fleet with latest safety technology",
    es: "Flota moderna con la última tecnología de seguridad",
  },
  "experience.value4": { en: "24/7 dispatch and customer support", es: "Despacho y soporte al cliente 24/7" },
  "experience.value5": { en: "Comprehensive insurance coverage", es: "Cobertura de seguro completa" },
  "experience.value6": { en: "Commitment to driver satisfaction", es: "Compromiso con la satisfacción del conductor" },
  "experience.milestone1": { en: "Company founded in Deptford, NJ", es: "Empresa fundada en Deptford, NJ" },
  "experience.milestone2": { en: "Expanded fleet and service area", es: "Expansión de flota y área de servicio" },
  "experience.milestone3": { en: "Achieved major fleet milestone", es: "Logro importante de flota" },
  "experience.milestone4": { en: "Continued growth and modernization", es: "Crecimiento continuo y modernización" },

  // Stats
  "stats.trucks": { en: "Active Trucks", es: "Camiones Activos" },
  "stats.drivers": { en: "Professional Drivers", es: "Conductores Profesionales" },
  "stats.states": { en: "States Covered", es: "Estados Cubiertos" },
  "stats.years": { en: "Years of Excellence", es: "Años de Excelencia" },

  // Drivers
  "drivers.tagline": { en: "Drive With Us", es: "Trabaja Con Nosotros" },
  "drivers.title": { en: "Join the Reyes Trux Corp Family", es: "Únete a la Familia Reyes Trux Corp" },
  "drivers.description": {
    en: "We're not just hiring drivers—we're building careers. Join a team that values your experience, respects your time, and rewards your dedication.",
    es: "No solo contratamos conductores, construimos carreras. Únete a un equipo que valora tu experiencia, respeta tu tiempo y recompensa tu dedicación.",
  },
  "drivers.pay.title": { en: "Competitive Pay", es: "Pago Competitivo" },
  "drivers.pay.desc": {
    en: "Industry-leading commission rates with weekly settlements. Competitive per mile rates plus bonuses.",
    es: "Tarifas de comisión líderes en la industria con liquidaciones semanales. Tarifas competitivas por milla más bonificaciones.",
  },
  "drivers.benefits.title": { en: "Full Benefits", es: "Beneficios Completos" },
  "drivers.benefits.desc": {
    en: "Comprehensive health, dental, and vision insurance for you and your family.",
    es: "Seguro médico, dental y de visión completo para ti y tu familia.",
  },
  "drivers.home.title": { en: "Home Time", es: "Tiempo en Casa" },
  "drivers.home.desc": {
    en: "We understand family comes first. Flexible scheduling with guaranteed home time.",
    es: "Entendemos que la familia es primero. Programación flexible con tiempo en casa garantizado.",
  },
  "drivers.equipment.title": { en: "Modern Equipment", es: "Equipo Moderno" },
  "drivers.equipment.desc": {
    en: "Drive late-model trucks with APUs, inverters, and latest safety technology.",
    es: "Conduce camiones de último modelo con APUs, inversores y la última tecnología de seguridad.",
  },
  "drivers.security.title": { en: "Job Security", es: "Seguridad Laboral" },
  "drivers.security.desc": {
    en: "Consistent freight year-round. No forced dispatch. Choose your loads and routes.",
    es: "Carga consistente todo el año. Sin despacho forzado. Elige tus cargas y rutas.",
  },
  "drivers.support.title": { en: "Team Support", es: "Apoyo del Equipo" },
  "drivers.support.desc": {
    en: "24/7 dispatch support, dedicated driver managers, and a community that feels like family.",
    es: "Soporte de despacho 24/7, gerentes de conductores dedicados y una comunidad que se siente como familia.",
  },
  "drivers.requirements": { en: "Requirements to Apply", es: "Requisitos para Aplicar" },
  "drivers.requirements.desc": {
    en: "We maintain high standards to ensure safety and quality. Here's what you need to join our team:",
    es: "Mantenemos altos estándares para garantizar seguridad y calidad. Esto es lo que necesitas para unirte:",
  },
  "drivers.req1": { en: "Valid Class A CDL", es: "Licencia CDL Clase A válida" },
  "drivers.req2": { en: "Minimum 2 years OTR experience", es: "Mínimo 2 años de experiencia OTR" },
  "drivers.req3": {
    en: "Clean MVR (no major violations in past 3 years)",
    es: "MVR limpio (sin violaciones mayores en los últimos 3 años)",
  },
  "drivers.req4": { en: "No DUI/DWI in past 5 years", es: "Sin DUI/DWI en los últimos 5 años" },
  "drivers.req5": {
    en: "Able to pass DOT physical and drug screening",
    es: "Capaz de pasar examen físico DOT y prueba de drogas",
  },
  "drivers.req6": {
    en: "Positive attitude and professional demeanor",
    es: "Actitud positiva y comportamiento profesional",
  },
  "drivers.perMile": { en: "Per Mile Commission", es: "Comisión Por Milla" },
  "drivers.bonuses": {
    en: "Plus sign-on bonuses and referral bonuses available",
    es: "Además bonos de contratación y bonos por referidos disponibles",
  },
  "drivers.applyNow": { en: "Apply Now", es: "Aplica Ahora" },

  // Vacancies Section
  "vacancies.tagline": { en: "Open Positions", es: "Posiciones Disponibles" },
  "vacancies.title": { en: "Current Job Openings", es: "Vacantes Actuales" },
  "vacancies.description": {
    en: "Browse our current openings and find the right opportunity for you.",
    es: "Explore nuestras vacantes actuales y encuentre la oportunidad adecuada para usted.",
  },
  "vacancies.applyNow": { en: "Apply Now", es: "Aplica Ahora" },
  "vacancies.requirements": { en: "Requirements", es: "Requisitos" },
  "vacancies.empty": {
    en: "No open positions at the moment. Check back soon or submit a general application below.",
    es: "No hay posiciones disponibles en este momento. Vuelva pronto o envíe una aplicación general abajo.",
  },
  "vacancies.location": { en: "Location", es: "Ubicación" },
  "vacancies.pay": { en: "Pay", es: "Pago" },

  // Application Form
  "apply.selectPosition": { en: "Select Position (Optional)", es: "Seleccionar Posición (Opcional)" },
  "apply.generalApplication": { en: "General Application", es: "Aplicación General" },
  "apply.tagline": { en: "Start Your Journey", es: "Comienza Tu Camino" },
  "apply.title": { en: "Driver Application Form", es: "Formulario de Aplicación para Conductores" },
  "apply.description": {
    en: "Complete the form below to start your application. Our recruiting team will review your information and contact you within 24-48 hours.",
    es: "Complete el formulario a continuación para iniciar su aplicación. Nuestro equipo de reclutamiento revisará su información y lo contactará dentro de 24-48 horas.",
  },
  "apply.commission": { en: "Commission Driver Application", es: "Aplicación de Conductor por Comisión" },
  "apply.required": {
    en: "All fields marked with * are required",
    es: "Todos los campos marcados con * son requeridos",
  },
  "apply.personal": { en: "Personal Information", es: "Información Personal" },
  "apply.firstName": { en: "First Name", es: "Nombre" },
  "apply.lastName": { en: "Last Name", es: "Apellido" },
  "apply.email": { en: "Email Address", es: "Correo Electrónico" },
  "apply.phone": { en: "Phone Number", es: "Número de Teléfono" },
  "apply.city": { en: "City", es: "Ciudad" },
  "apply.state": { en: "State", es: "Estado" },
  "apply.selectState": { en: "Select state", es: "Seleccionar estado" },
  "apply.experience": { en: "Driving Experience", es: "Experiencia de Conducción" },
  "apply.cdlClass": { en: "CDL Class", es: "Clase de CDL" },
  "apply.selectCdl": { en: "Select CDL class", es: "Seleccionar clase de CDL" },
  "apply.yearsExp": { en: "Years of OTR Experience", es: "Años de Experiencia OTR" },
  "apply.selectExp": { en: "Select experience", es: "Seleccionar experiencia" },
  "apply.equipment": { en: "Equipment Preference", es: "Preferencia de Equipo" },
  "apply.selectEquip": { en: "Select preference", es: "Seleccionar preferencia" },
  "apply.startDate": { en: "Available Start Date", es: "Fecha Disponible de Inicio" },
  "apply.endorsements": {
    en: "I have additional endorsements (Hazmat, Tanker, Doubles/Triples, etc.)",
    es: "Tengo endosos adicionales (Hazmat, Tanker, Doubles/Triples, etc.)",
  },
  "apply.listEndorsements": { en: "List your endorsements", es: "Liste sus endosos" },
  "apply.currentlyEmployed": {
    en: "I am currently employed as a driver",
    es: "Actualmente estoy empleado como conductor",
  },
  "apply.additional": { en: "Additional Information", es: "Información Adicional" },
  "apply.violations": {
    en: "Any accidents or violations in the past 3 years? Please describe.",
    es: "¿Algún accidente o violación en los últimos 3 años? Por favor describa.",
  },
  "apply.violationsPlaceholder": {
    en: "Describe any accidents or violations, or type 'None' if applicable",
    es: "Describa cualquier accidente o violación, o escriba 'Ninguno' si aplica",
  },
  "apply.whyJoin": { en: "Why do you want to join Reyes Trux Corp?", es: "¿Por qué quieres unirte a Reyes Trux Corp?" },
  "apply.whyJoinPlaceholder": {
    en: "Tell us about yourself and why you'd be a great fit for our team",
    es: "Cuéntanos sobre ti y por qué serías ideal para nuestro equipo",
  },
  "apply.documents": {
    en: "Documents Required After Initial Review",
    es: "Documentos Requeridos Después de la Revisión Inicial",
  },
  "apply.documentsDesc": {
    en: "If your application is approved for the next step, you'll need to provide: CDL copy, MVR report, medical card, and employment verification for the past 3 years.",
    es: "Si su aplicación es aprobada para el siguiente paso, deberá proporcionar: copia de CDL, informe MVR, tarjeta médica y verificación de empleo de los últimos 3 años.",
  },
  "apply.terms": {
    en: "I certify that the information provided is accurate and complete. I understand that misrepresentation may result in disqualification or termination. I consent to background checks and drug screening as part of the hiring process.",
    es: "Certifico que la información proporcionada es precisa y completa. Entiendo que la tergiversación puede resultar en descalificación o terminación. Consiento verificaciones de antecedentes y pruebas de drogas como parte del proceso de contratación.",
  },
  "apply.saveDraft": { en: "Save as Draft", es: "Guardar Borrador" },
  "apply.submit": { en: "Submit Application", es: "Enviar Aplicación" },
  "apply.success.title": { en: "Application Submitted!", es: "¡Aplicación Enviada!" },
  "apply.success.desc": {
    en: "Thank you for your interest in joining Reyes Trux Corp. Our recruiting team will review your application and contact you within 24-48 hours.",
    es: "Gracias por su interés en unirse a Reyes Trux Corp. Nuestro equipo de reclutamiento revisará su aplicación y lo contactará dentro de 24-48 horas.",
  },
  "apply.submitAnother": { en: "Submit Another Application", es: "Enviar Otra Aplicación" },
  "apply.dryVan": { en: "Dry Van", es: "Dry Van" },
  "apply.reefer": { en: "Refrigerated", es: "Refrigerado" },
  "apply.flatbed": { en: "Flatbed", es: "Flatbed" },
  "apply.tanker": { en: "Tanker", es: "Tanker" },
  "apply.noPreference": { en: "No Preference", es: "Sin Preferencia" },
  "apply.classA": { en: "Class A", es: "Clase A" },
  "apply.classB": { en: "Class B", es: "Clase B" },
  "apply.permit": { en: "CDL Permit (Class A)", es: "Permiso CDL (Clase A)" },
  "apply.exp0": { en: "Less than 1 year", es: "Menos de 1 año" },
  "apply.exp1": { en: "1-2 years", es: "1-2 años" },
  "apply.exp2": { en: "2-5 years", es: "2-5 años" },
  "apply.exp5": { en: "5-10 years", es: "5-10 años" },
  "apply.exp10": { en: "10+ years", es: "10+ años" },

  // Testimonials
  "testimonials.tagline": { en: "Testimonials", es: "Testimonios" },
  "testimonials.title": { en: "What Our Drivers & Clients Say", es: "Lo Que Dicen Nuestros Conductores y Clientes" },
  "testimonials.driver": { en: "Driver", es: "Conductor" },
  "testimonials.client": { en: "Client", es: "Cliente" },

  // Contact
  "contact.tagline": { en: "Get In Touch", es: "Contáctanos" },
  "contact.title": { en: "Request a Quote or Contact Us", es: "Solicita una Cotización o Contáctanos" },
  "contact.description": {
    en: "Ready to ship? Have questions? Our team is here to help 24/7. Fill out the form below or reach us directly.",
    es: "¿Listo para enviar? ¿Tienes preguntas? Nuestro equipo está aquí para ayudar 24/7. Completa el formulario o contáctanos directamente.",
  },
  "contact.headquarters": { en: "Headquarters", es: "Oficina Principal" },
  "contact.phone": { en: "Phone", es: "Teléfono" },
  "contact.email": { en: "Email", es: "Correo" },
  "contact.hours": { en: "Hours", es: "Horario" },
  "contact.operations": { en: "Operations: 24/7", es: "Operaciones: 24/7" },
  "contact.office": { en: "Office: Mon-Fri 8AM-6PM EST", es: "Oficina: Lun-Vie 8AM-6PM EST" },
  "contact.fullName": { en: "Full Name", es: "Nombre Completo" },
  "contact.company": { en: "Company", es: "Empresa" },
  "contact.subject": { en: "Subject", es: "Asunto" },
  "contact.subjectPlaceholder": { en: "What can we help you with?", es: "¿En qué podemos ayudarte?" },
  "contact.message": { en: "Message", es: "Mensaje" },
  "contact.messagePlaceholder": {
    en: "Tell us about your shipping needs, questions, or how we can help...",
    es: "Cuéntanos sobre tus necesidades de envío, preguntas o cómo podemos ayudar...",
  },
  "contact.send": { en: "Send Message", es: "Enviar Mensaje" },
  "contact.sent.title": { en: "Message Sent!", es: "¡Mensaje Enviado!" },
  "contact.sent.desc": {
    en: "Thank you for reaching out. We'll get back to you within 24 hours.",
    es: "Gracias por contactarnos. Te responderemos dentro de 24 horas.",
  },
  "contact.sendAnother": { en: "Send Another Message", es: "Enviar Otro Mensaje" },
  "contact.quote": { en: "Request a Quote", es: "Solicitar Cotización" },
  "contact.serviceInquiry": { en: "Service Inquiry", es: "Consulta de Servicio" },
  "contact.partnership": { en: "Partnership Opportunity", es: "Oportunidad de Sociedad" },
  "contact.careers": { en: "Career Inquiry", es: "Consulta de Carrera" },
  "contact.support": { en: "Customer Support", es: "Soporte al Cliente" },
  "contact.other": { en: "Other", es: "Otro" },

  // Footer
  "footer.description": {
    en: "Delivering excellence across every mile. Your trusted partner for reliable freight solutions and rewarding driving careers.",
    es: "Entregando excelencia en cada milla. Su socio de confianza para soluciones de carga confiables y carreras de conducción gratificantes.",
  },
  "footer.services": { en: "Services", es: "Servicios" },
  "footer.company": { en: "Company", es: "Empresa" },
  "footer.forDrivers": { en: "For Drivers", es: "Para Conductores" },
  "footer.fullTruckload": { en: "Full Truckload", es: "Carga Completa" },
  "footer.ltlShipping": { en: "LTL Shipping", es: "Carga Parcial" },
  "footer.expeditedFreight": { en: "Expedited Freight", es: "Carga Expedita" },
  "footer.regional": { en: "Regional Routes", es: "Rutas Regionales" },
  "footer.specialized": { en: "Specialized Hauling", es: "Transporte Especializado" },
  "footer.aboutUs": { en: "About Us", es: "Nosotros" },
  "footer.ourFleet": { en: "Our Fleet", es: "Nuestra Flota" },
  "footer.careers": { en: "Careers", es: "Carreras" },
  "footer.news": { en: "News & Updates", es: "Noticias" },
  "footer.driveWithUs": { en: "Drive With Us", es: "Trabaja Con Nosotros" },
  "footer.benefits": { en: "Benefits", es: "Beneficios" },
  "footer.requirements": { en: "Requirements", es: "Requisitos" },
  "footer.driverPortal": { en: "Driver Portal", es: "Portal del Conductor" },
  "footer.privacy": { en: "Privacy Policy", es: "Política de Privacidad" },
  "footer.terms": { en: "Terms of Service", es: "Términos de Servicio" },
  "footer.cookies": { en: "Cookie Policy", es: "Política de Cookies" },
  "footer.accessibility": { en: "Accessibility", es: "Accesibilidad" },
  "footer.rights": { en: "All rights reserved.", es: "Todos los derechos reservados." },
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>("en");

  const t = (key: string): string => {
    const translation = translations[key];
    if (!translation) {
      console.warn(`Translation missing for key: ${key}`);
      return key;
    }
    return translation[language];
  };

  return <LanguageContext.Provider value={{ language, setLanguage, t }}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
